import { useState } from 'react';
import useLogin from '../../hooks/use-login';
import { INITIAL_LOGIN_FORM, type LoginFormState } from './login-page.types';
import { buildLoginArgs, updateLoginField } from './login-page.utils';

/**
 * Architectural summary: Owns LoginPage form state and submit/change handlers
 * while delegating authentication side effects to useLogin.
 */
export function useLoginPageController() {
  const [form, setForm] = useState<LoginFormState>(INITIAL_LOGIN_FORM);
  const { handleLogin, isLoading, error } = useLogin();
  const [validationErrors, setValidationErrors] = useState<{
    username: string | null;
    password: string | null;
  }>({
    username: null,
    password: null,
  });

  /**
   * Architectural summary: Submits current username/password draft.
   */
  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const nextValidationErrors = {
      username: form.username.trim().length === 0 ? 'יש להזין שם משתמש' : null,
      password: form.password.trim().length === 0 ? 'יש להזין סיסמה' : null,
    };

    setValidationErrors(nextValidationErrors);

    if (nextValidationErrors.username || nextValidationErrors.password) {
      return;
    }

    await handleLogin(...buildLoginArgs(form));
  };

  /**
   * Architectural summary: Applies username input changes immutably.
   */
  const onChangeUsername = (value: string) => {
    setForm((previous) => updateLoginField(previous, 'username', value));
    if (value.trim().length > 0) {
      setValidationErrors((previous) => ({ ...previous, username: null }));
    }
  };

  /**
   * Architectural summary: Applies password input changes immutably.
   */
  const onChangePassword = (value: string) => {
    setForm((previous) => updateLoginField(previous, 'password', value));
    if (value.trim().length > 0) {
      setValidationErrors((previous) => ({ ...previous, password: null }));
    }
  };

  return {
    form,
    validationErrors,
    isLoading,
    error,
    onSubmit,
    onChangeUsername,
    onChangePassword,
  };
}
